// How days, deadlines, times, lists and places read on screen, in English and German (Components.swift: DeadlineLabel,
// StartLabel, PlaceLabel; the list titles of Route).

import { ageOn, congratulatedIn } from "../core/birthdays";
import { addDays, dayParts, daysBetween, timeOf, weekday } from "../core/days";
import type { ListHeader, ListText, Lists, Route } from "../core/lists";
import { isList, type Area, type Birthday, type Project, type Todo } from "../core/records";
import type { Day, RepeatRule, Start, Weekday } from "../core/types";
import { currentLanguage, key, locale, t } from "../i18n";
import { activeMonthDays } from "../core/repeats";
import { monthDaysPlace } from "../core/parser/labels";
import type { IconName } from "./Icon";
import { syncMoment, type SyncStatus } from "../core/syncStatus";

/** A day as a UTC date, for Intl (which then never shifts it). */
function asDate(day: Day): Date {
  const { year, month, date } = dayParts(day);
  return new Date(Date.UTC(year, month - 1, date, 12));
}

export function formatDay(day: Day, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale(), { ...options, timeZone: "UTC" }).format(asDate(day));
}

/**
 * The first day of the locale's week, as Calendar counts (1 = Sunday, 2 = Monday): where "this week" begins for the
 * Inbox's figures, as the apps take it from the device's calendar. Monday where the browser can't say.
 */
export function firstWeekday(): Weekday {
  try {
    const tag = new Intl.Locale(locale()) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    const first = tag.getWeekInfo?.().firstDay ?? tag.weekInfo?.firstDay;
    if (first !== undefined && first >= 1 && first <= 7) return ((first % 7) + 1) as Weekday;
  } catch {
    // An unknown locale: Monday.
  }
  return 2;
}

/** "Thursday, 29 October", "Donnerstag, 29. Oktober": the date under Today's and Habits' title. */
export function longDate(day: Day): string {
  return formatDay(day, { weekday: "long", day: "numeric", month: "long" });
}

/** Today's date beside its title, without the year, as the browser's language and region write it: "1 Nov", "Nov 1",
 * "1. Nov." (Bastian, 2026-11-01: from the user's settings, as in the apps). */
export function titleDate(day: Day): string {
  return formatDay(day, { day: "numeric", month: "short" });
}

/** "12 Oct", with the year when it isn't this one. */
export function shortDate(day: Day, today: Day): string {
  const sameYear = dayParts(day).year === dayParts(today).year;
  return formatDay(day, sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}

/** The deadline beside a title: "today", "3d ago", "2d left" (DeadlineLabel). */
export function deadlineLabel(deadline: Day, today: Day): string {
  const days = daysBetween(today, deadline);
  if (days === 0) return t("today");
  if (days < 0) return t("{count}d ago", { count: -days });
  return t("{count}d left", { count: days });
}

/** The deadline on the open to-do's chip: "Due Today" rather than a second "Today" beside the When chip's (the UX
 * review, 2026-10-24); otherwise as in the rows. */
export function deadlineChipLabel(deadline: Day, today: Day): string {
  return daysBetween(today, deadline) === 0 ? t("Due Today") : deadlineLabel(deadline, today);
}

/** The deadline in words, for screen readers. */
export function deadlineSpoken(deadline: Day, today: Day): string {
  const days = daysBetween(today, deadline);
  if (days === 0) return t("Deadline today");
  if (days === 1) return t("Deadline tomorrow");
  if (days < 0) return t("Deadline {count} days ago", { count: -days });
  return t("Deadline in {count} days", { count: days });
}

/** A later start beside a title: "Tomorrow", a weekday within the week, a date beyond it (StartLabel). */
export function startLabel(day: Day, today: Day): string {
  const days = daysBetween(today, day);
  if (days === 0) return t("Today");
  if (days === 1) return t("Tomorrow");
  if (days > 1 && days < 7) return formatDay(day, { weekday: "short" });
  return shortDate(day, today);
}

/** What the When chip says. */
export function whenLabel(start: Start, today: Day): string | null {
  switch (start.kind) {
    case "inbox": return null;
    case "anytime": return t("Anytime");
    case "someday": return t("Someday");
    case "on": return start.day <= today ? t("Today") : startLabel(start.day, today);
  }
}

export function whenIcon(start: Start, today: Day): IconName {
  switch (start.kind) {
    case "anytime": return "anytime";
    case "someday": return "someday";
    case "on": return start.day <= today ? "today" : "calendar";
    default: return "inbox";
  }
}

/** A day heading: "Tomorrow" with "Mon 12 Oct" beside it; a weekday further on; "Today"/"Yesterday" in the Archive. */
export function dayHeading(day: Day, today: Day): { title: string; detail?: string } {
  const days = daysBetween(today, day);
  const full = formatDay(day, { weekday: "short", day: "numeric", month: "short" });
  if (days === 0) return { title: t("Today"), detail: full };
  if (days === 1) return { title: t("Tomorrow"), detail: full };
  if (days === -1) return { title: t("Yesterday"), detail: full };
  if (Math.abs(days) <= 7) return { title: formatDay(day, { weekday: "long" }), detail: shortDate(day, today) };
  return { title: formatDay(day, { weekday: "long", day: "numeric", month: "long" }) };
}

export function monthTitle(day: Day, today: Day): string {
  const sameYear = dayParts(day).year === dayParts(today).year;
  return formatDay(day, sameYear ? { month: "long" } : { month: "long", year: "numeric" });
}

/** A time of day, as the browser's locale writes it. */
export function timeLabel(moment: Date, zone: string): string {
  return new Intl.DateTimeFormat(locale(), { hour: "numeric", minute: "2-digit", timeZone: zone }).format(moment);
}

export function hourLabel(hour: number, minute = 0): string {
  return new Intl.DateTimeFormat(locale(), { hour: "numeric", minute: "2-digit", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 1, hour, minute)));
}

export { timeOf };

/**
 * The moment the cloud in the list's bar says (docs/64, SyncStatusRule.words): "09:17" today, "yesterday, 18:40"
 * ("Yesterday, 18:40" in a title), "25 Oct", with the year when it isn't this one.
 */
export function syncMomentLabel(since: number, now: number, zone: string, titled: boolean): string {
  const moment = syncMoment(since, now, zone);
  const time = timeLabel(new Date(since), zone);
  switch (moment.kind) {
    case "today": return time;
    case "yesterday": return titled ? t("Yesterday, {time}", { time }) : t("yesterday, {time}", { time });
    case "thisYear": return new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", timeZone: zone }).format(new Date(since));
    case "earlier": return new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", year: "numeric", timeZone: zone }).format(new Date(since));
  }
}

/** The cloud's label and tooltip: "Not synced since 09:17". */
export function syncLabel(status: SyncStatus, now: number, zone: string): string {
  const time = syncMomentLabel(status.since, now, zone, false);
  switch (status.kind) {
    case "notSynced": return t("Not synced since {time}", { time });
    case "offline": return t("Offline since {time}", { time });
    case "update": return t("Update Todolix to sync");
  }
}

/** The popover's title: "Not Synced Since 09:17". */
export function syncTitle(status: SyncStatus, now: number, zone: string): string {
  const time = syncMomentLabel(status.since, now, zone, true);
  switch (status.kind) {
    case "notSynced": return t("Not Synced Since {time}", { time });
    case "offline": return t("Offline Since {time}", { time });
    case "update": return t("Update Todolix to sync");
  }
}

/** How a repeat reads on its chip: "Every week", "Every 2 days", "Every Mon, Wed". */
export function repeatLabel(rule: RepeatRule): string {
  const unitWord = (count: number) => {
    switch (rule.unit) {
      case "day": return t("Every {count} days", { count });
      case "week": return t("Every {count} weeks", { count });
      case "month": return t("Every {count} months", { count });
      case "year": return t("Every {count} years", { count });
    }
  };
  if (rule.unit === "week" && rule.weekdays && rule.weekdays.length > 0) {
    const names = [...rule.weekdays].sort((a, b) => ((a + 5) % 7) - ((b + 5) % 7))
      .map((day) => formatDay(addDays("2026-10-04" as Day, day - 1), { weekday: "short" }));
    return t("Every {days}", { days: names.join(", ") });
  }
  const monthDays = activeMonthDays(rule);
  if (monthDays) {
    // "Every month on the 15th", "Jeden Monat am 1. und 15." (RepeatRule.title, docs/67).
    return t("Every {count} months on {place}", { count: rule.interval, place: monthDaysPlace(monthDays, currentLanguage()) });
  }
  if (rule.unit === "month" && rule.monthPosition) {
    const position = rule.monthPosition;
    const ordinal = position.ordinal === -1 ? t("last") : [t("first"), t("second"), t("third"), t("fourth")][position.ordinal - 1] ?? "";
    const what = position.weekday ? formatDay(addDays("2026-10-04" as Day, position.weekday - 1), { weekday: "long" }) : t("day");
    return t("Every {ordinal} {what} of the month", { ordinal, what });
  }
  return unitWord(rule.interval);
}

// MARK: - Lists

const BUILT_IN: Record<string, { title: string; icon: IconName }> = {
  inbox: { title: key("Inbox"), icon: "inbox" },
  today: { title: key("Today"), icon: "today" },
  due: { title: key("Deadlines"), icon: "due" },
  scheduled: { title: key("Scheduled"), icon: "scheduled" },
  anytime: { title: key("Anytime"), icon: "anytime" },
  someday: { title: key("Someday"), icon: "someday" },
  stale: { title: key("Stale"), icon: "stale" },
  archive: { title: key("Archive"), icon: "archive" },
  general: { title: key("Miscellaneous"), icon: "misc" },
  templates: { title: key("Templates"), icon: "templates" },
  habits: { title: key("Habits"), icon: "flame" },
};

export function routeTitle(route: Route, lists: Lists): string {
  if (route.kind === "project") return lists.project(route.id)?.title ?? "";
  if (route.kind === "area") return lists.area(route.id)?.title ?? "";
  if (route.kind === "search") return t("Find");
  const builtIn = BUILT_IN[route.kind];
  return builtIn ? t(builtIn.title) : "";
}

export function routeIcon(route: Route): IconName {
  return BUILT_IN[route.kind]?.icon ?? "list";
}

export function sameRoute(a: Route, b: Route): boolean {
  if (a.kind !== b.kind) return false;
  if ((a.kind === "project" || a.kind === "area" || a.kind === "template") && (b.kind === "project" || b.kind === "area" || b.kind === "template")) return a.id === b.id;
  return true;
}

export function listText(text: ListText): string {
  switch (text) {
    case "projects": return t("Projects");
    case "todos": return t("To-Dos");
    case "completed": return t("Completed");
    case "overdue": return t("Overdue");
    case "due": return t("Due Today");
    case "upcomingDeadlines": return t("Upcoming");
    case "today": return t("Today");
    case "scheduled": return t("Scheduled");
    case "anytime": return t("Anytime");
    case "someday": return t("Someday");
    case "habits": return t("Habits");
  }
}

/** An area's name with its emoji apart, as Home shows it ("💼 Work" → 💼, Work): the core's, docs/61. */
export { splitEmoji } from "../core/areaIcons";

/** Where a to-do lives, for lists that mix places (PlaceLabel): its project, its area, the Inbox or Miscellaneous. */
export function placeOf(todo: Todo, lists: Lists): { kind: "project" | "list" | "birthdays" | "area" | "inbox" | "general"; title: string; project?: Project; area?: Area } {
  const project = lists.project(todo.projectID);
  if (project) return { kind: project.kind === "birthdays" ? "birthdays" : isList(project) ? "list" : "project", title: project.title, project };
  const area = lists.area(todo.areaID);
  if (area) return { kind: "area", title: area.title, area };
  if (todo.start.kind === "inbox") return { kind: "inbox", title: t("Inbox") };
  return { kind: "general", title: t("Miscellaneous") };
}

/**
 * Where a row says a to-do lives (Store.rowPlaceName(for:)): `placeOf` for a to-do in a project or an area, and nothing
 * for a loose one or one in the Inbox (docs/65, A6: no "Miscellaneous" on rows; a row without a place is loose). A to-do
 * to congratulate in the Birthdays list names the person beside the gift ("Jonathan Selbie" rather than "Birthdays",
 * docs/59, 5); a birthday that is gone or hidden leaves "Birthdays".
 */
export function rowPlaceOf(todo: Todo, lists: Lists): ReturnType<typeof placeOf> | null {
  const place = placeOf(todo, lists);
  if (place.kind === "general" || place.kind === "inbox") return null;
  if (place.kind !== "birthdays") return place;
  // Whose birthday and what it is: "Anna Berger turns 36", "Ben Birthday" (Store.congratulationWords(of:); the display
  // decisions of 2026-10-29, point 12 E: the words that stood in Today's header).
  const found = congratulatedIn(lists, todo.id);
  return found ? { ...place, title: `${found.birthday.name} ${birthdayLabel(found.birthday, found.day)}` } : place;
}

/** "turns 36" on the day, or "Birthday" without a year of birth (Birthday.label(on:)). */
export function birthdayLabel(birthday: Birthday, day: Day): string {
  const age = ageOn(birthday, day);
  return age === undefined ? t("Birthday") : t("turns {count}", { count: age });
}

/** Whether a list shows where each to-do lives (it mixes places and doesn't group by them). */
export function showsPlace(route: Route, header: ListHeader): boolean {
  if (header.kind === "project" || header.kind === "area" || header.kind === "general") return false;
  return ["today", "scheduled", "due", "archive", "search", "stale", "anytime", "someday"].includes(route.kind);
}

export function weekdayOf(day: Day) {
  return weekday(day);
}
